import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shield, ShieldCheck, Crown, ArrowLeft, Loader2, Check, Sparkles, Zap, Users, XCircle, RotateCcw, CalendarX } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoatTeamPanel from "@/components/GoatTeamPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TIERS = [
  {
    id: "recruit",
    name: "The Recruit",
    tagline: "Get started, hero.",
    monthly: 0,
    yearly: 0,
    accent: "slate",
    icon: Shield,
    features: [
      "Connect 1 bank via Plaid",
      "Track & manage up to 2 subscriptions",
      "Defeat up to 2 bills",
      "Hero Score gauge & weekly streak",
    ],
  },
  {
    id: "guardian",
    name: "The Guardian",
    tagline: "The full money-protection kit.",
    monthly: 6.99,
    yearly: 67.10,
    yearlyMonths: 12,
    accent: "gold",
    popular: true,
    icon: ShieldCheck,
    features: [
      "Connect up to 2 bank accounts",
      "Track & manage up to 5 subscriptions",
      "Unlimited bill defeats",
      "Hero Alerts when prices hike",
      "Priority weekly Boss Battle slot",
    ],
  },
  {
    id: "legend",
    name: "The Legend",
    tagline: "For households & true protectors.",
    monthly: 14.99,
    yearly: 143.90,
    yearlyMonths: 12,
    accent: "midnight",
    icon: Crown,
    features: [
      "Everything in Guardian",
      "Connect up to 6 bank accounts",
      "Scan & manage up to 15 subscriptions",
      "Priority Cancel Support — we call your providers",
      "Concierge audit & savings plan",
    ],
  },
  {
    id: "goat",
    name: "G.O.A.T",
    tagline: "Unlimited reach for families & businesses.",
    monthly: 39.99,
    yearly: 299.99,
    yearlyMonths: 12,
    accent: "goat",
    icon: Crown,
    badge: "New",
    features: [
      "Everything in Legend",
      "Unlimited linked bank accounts",
      "Unlimited subscription tracking",
      "Up to 20 invited members (family or employees)",
      "Roll-up dashboard for the GOAT owner",
      "Coming soon — invite team members from your dashboard",
    ],
  },
];

const ACCENTS = {
  slate: { ring: "ring-slate-200", chipBg: "bg-slate-100", chipText: "text-slate-700", priceText: "text-slate-900", btn: "bg-slate-900 hover:bg-slate-800 text-white" },
  gold:  { ring: "ring-amber-300", chipBg: "bg-amber-100", chipText: "text-amber-800", priceText: "text-slate-900", btn: "" /* gradient inline */ },
  midnight: { ring: "ring-slate-300", chipBg: "bg-slate-900", chipText: "text-amber-300", priceText: "text-slate-900", btn: "bg-slate-900 hover:bg-slate-800 text-white" },
  goat: { ring: "ring-fuchsia-300", chipBg: "bg-gradient-to-r from-amber-400 to-fuchsia-500", chipText: "text-white", priceText: "text-slate-900", btn: "bg-gradient-to-r from-amber-400 via-fuchsia-500 to-violet-600 hover:opacity-95 text-white" },
};

function priceFor(tier, interval) {
  if (tier.id === "recruit") return { primary: "Free", secondary: "Forever" };
  if (interval === "monthly") {
    return { primary: `$${tier.monthly.toFixed(2)}`, secondary: "/month" };
  }
  // yearly
  const monthlyEquiv = (tier.yearly / 12).toFixed(2);
  return {
    primary: `$${tier.yearly.toFixed(2)}`,
    secondary: "/year",
    aside: `$${monthlyEquiv}/mo · save 20%`,
  };
}

export default function Upgrade() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [params, setParams] = useSearchParams();
  const [interval, setInterval] = useState("yearly");
  const [busyPkg, setBusyPkg] = useState(null);
  const [polling, setPolling] = useState(false);

  const sessionId = params.get("session_id");
  const status = params.get("status");
  const currentTier = (user && user !== false && user.tier) || "recruit";
  const subStatus = (user && user !== false && user.subscription_status) || null;
  const currentPeriodEnd = user && user !== false && user.current_period_end
    ? new Date(user.current_period_end)
    : null;
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  // After redirect from Stripe, poll for status
  useEffect(() => {
    if (!sessionId || status !== "success") return;
    let cancelled = false;
    let attempts = 0;
    const max = 7;
    const pollInterval = 2000;

    setPolling(true);
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          toast.success("Welcome aboard!", {
            description: `You're now on the ${(data.tier || "").toUpperCase()} plan.`,
            duration: 5000,
          });
          await refreshMe();
          setPolling(false);
          // strip query so refresh doesn't re-poll
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired. Please try again.");
          setPolling(false);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        console.error("poll status failed", e);
      }
      if (attempts < max) {
        window.setTimeout(tick, pollInterval);
      } else {
        toast.message("Still confirming your payment", {
          description: "We'll update your plan as soon as Stripe confirms.",
        });
        setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, status]);

  // Cancel toast on return from Stripe
  useEffect(() => {
    if (status === "cancel") {
      toast.message("Checkout cancelled", { description: "No charges were made." });
      setParams({}, { replace: true });
    }
  }, [status, setParams]);

  const startCheckout = async (tier) => {
    if (tier.id === "recruit") {
      toast.message("You're already on the free plan.");
      return;
    }
    const pkgId = `${tier.id}_${interval}`;
    setBusyPkg(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL");
      window.location.href = data.url; // hard redirect to Stripe
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusyPkg(null);
    }
  };

  const yearlySavings = useMemo(() => {
    const guard = TIERS.find((t) => t.id === "guardian");
    return Math.round((guard.monthly * 12 - guard.yearly) * 100) / 100;
  }, []);

  const formatPeriodEnd = (d) => {
    if (!d) return "your next renewal";
    try {
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "your next renewal";
    }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      const { data } = await api.post("/payments/cancel");
      toast.message("Plan canceled", {
        description: data?.message || `Access continues until ${formatPeriodEnd(currentPeriodEnd)}.`,
        duration: 6000,
      });
      await refreshMe();
      setCancelOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not cancel. Try again.");
    } finally {
      setCancelBusy(false);
    }
  };

  const doReactivate = async () => {
    setCancelBusy(true);
    try {
      const { data } = await api.post("/payments/reactivate");
      toast.success("Plan reactivated!", {
        description: data?.message || "You're back in action, hero.",
        duration: 5000,
      });
      await refreshMe();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not reactivate. Try again.");
    } finally {
      setCancelBusy(false);
    }
  };

  const isPaid = currentTier !== "recruit";
  const isCanceled = isPaid && (subStatus === "canceled");
  const isGoatOwner = currentTier === "goat" && !((user && user !== false && user.owner_user_id) || null);

  return (
    <div className="hero-theme min-h-screen px-5 py-8">
      <div className="max-w-md mx-auto w-full pb-16">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
          data-testid="upgrade-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>

        <header className="mb-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">Power up</p>
          <h1
            className="text-4xl font-black text-slate-900 leading-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Choose your<br />hero tier.
          </h1>
          <p className="mt-2 text-sm font-medium text-slate-500 leading-relaxed">
            Cancel anytime. Save up to 20% with yearly. Plans renew automatically — switch or downgrade in one tap.
          </p>
        </header>

        {/* Toggle */}
        <div
          className="flex items-center justify-center gap-1 p-1 rounded-2xl bg-slate-900 text-white max-w-xs mx-auto"
          data-testid="billing-toggle"
        >
          <button
            type="button"
            onClick={() => setInterval("monthly")}
            className={`flex-1 h-10 rounded-xl text-sm font-extrabold tracking-wide transition-all ${
              interval === "monthly" ? "bg-white text-slate-900 shadow" : "text-slate-300 hover:text-white"
            }`}
            data-testid="billing-toggle-monthly"
            aria-pressed={interval === "monthly"}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval("yearly")}
            className={`flex-1 h-10 rounded-xl text-sm font-extrabold tracking-wide transition-all relative ${
              interval === "yearly" ? "bg-amber-400 text-slate-900 shadow" : "text-slate-300 hover:text-white"
            }`}
            data-testid="billing-toggle-yearly"
            aria-pressed={interval === "yearly"}
          >
            Yearly
            <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[9px] font-black border-2 border-[#F8FAFC]">
              -20%
            </span>
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] font-semibold text-slate-500">
          Yearly Guardians save ${yearlySavings.toFixed(2)} a year.
        </p>

        {/* Manage plan card (paid users only) */}
        {isPaid && (
          <div
            className={`mt-6 rounded-3xl border-2 p-5 ${
              isCanceled ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"
            }`}
            data-testid="manage-plan-card"
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  isCanceled ? "bg-amber-400 text-slate-900" : "bg-slate-900 text-amber-400"
                }`}
              >
                {isCanceled ? <CalendarX className="w-5 h-5" strokeWidth={2.6} /> : <Crown className="w-5 h-5" strokeWidth={2.6} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Your plan
                </p>
                <h3
                  className="text-xl font-black text-slate-900 leading-tight mt-0.5"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="manage-plan-heading"
                >
                  {isCanceled ? "Canceled" : "Active"} · {currentTier.toUpperCase()}
                </h3>
                <p className="text-xs font-semibold text-slate-600 mt-1 leading-snug" data-testid="manage-plan-subtext">
                  {isCanceled
                    ? `Hero powers continue until ${formatPeriodEnd(currentPeriodEnd)}. After that you'll drop back to Recruit.`
                    : currentPeriodEnd
                      ? `Renews ${formatPeriodEnd(currentPeriodEnd)}. Cancel anytime — keep access until period ends.`
                      : "Your Hero plan is active."}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              {isCanceled ? (
                <Button
                  onClick={doReactivate}
                  disabled={cancelBusy}
                  className="flex-1 h-11 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold gap-2 disabled:opacity-70"
                  data-testid="reactivate-plan-button"
                >
                  {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" strokeWidth={2.8} />}
                  Reactivate plan
                </Button>
              ) : (
                <Button
                  onClick={() => setCancelOpen(true)}
                  variant="ghost"
                  disabled={cancelBusy}
                  className="flex-1 h-11 rounded-2xl text-slate-700 hover:text-red-600 hover:bg-red-50 font-extrabold gap-2 border-2 border-slate-200 hover:border-red-200"
                  data-testid="cancel-plan-button"
                >
                  <XCircle className="w-4 h-4" strokeWidth={2.8} />
                  Cancel plan
                </Button>
              )}
            </div>
          </div>
        )}

        {/* GOAT Team panel — owners only */}
        {isGoatOwner && (
          <div className="mt-6">
            <GoatTeamPanel onChange={refreshMe} />
          </div>
        )}

        {/* Tiers */}
        <div className="mt-8 flex flex-col gap-5" data-testid="pricing-tiers">
          {TIERS.map((tier) => {
            const acc = ACCENTS[tier.accent];
            const Icon = tier.icon;
            const price = priceFor(tier, interval);
            const pkgId = `${tier.id}_${interval}`;
            const isBusy = busyPkg === pkgId;
            const isCurrent = currentTier === tier.id;
            const isPopular = tier.popular;

            return (
              <div
                key={tier.id}
                className={`relative rounded-3xl border-2 p-6 transition-all ${
                  isPopular
                    ? "scale-[1.02] border-amber-400 shadow-2xl shadow-amber-400/30"
                    : "border-slate-100 shadow-sm hover:shadow-md"
                }`}
                style={
                  isPopular
                    ? { background: "linear-gradient(160deg, #FFFFFF 0%, #FFF7ED 75%, #FEF3C7 100%)" }
                    : { background: "#FFFFFF" }
                }
                data-testid={`tier-card-${tier.id}`}
                data-popular={isPopular ? "true" : "false"}
              >
                {isPopular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-900 inline-flex items-center gap-1 border-2 border-white shadow-md"
                    style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B)" }}
                    data-testid="popular-badge"
                  >
                    <Sparkles className="w-3 h-3" strokeWidth={3} /> Most Popular
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ring-4 ${acc.ring} ${
                      isPopular
                        ? "bg-amber-400 text-slate-900"
                        : tier.accent === "midnight"
                          ? "bg-slate-900 text-amber-400"
                          : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    <Icon className="w-6 h-6" strokeWidth={2.4} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      className="text-2xl font-black text-slate-900 leading-tight"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                      data-testid={`tier-name-${tier.id}`}
                    >
                      {tier.name}
                    </h2>
                    <p className="text-xs font-semibold text-slate-500 mt-0.5">{tier.tagline}</p>
                  </div>
                </div>

                <div className="mt-5 flex items-baseline gap-1">
                  <span
                    className="text-5xl font-black text-slate-900 leading-none tracking-tight"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    data-testid={`tier-price-${tier.id}`}
                  >
                    {price.primary}
                  </span>
                  <span className="text-sm font-bold text-slate-500 ml-1">{price.secondary}</span>
                </div>
                {price.aside && (
                  <p className="text-xs font-bold text-amber-700 mt-1">
                    {price.aside}
                  </p>
                )}

                <ul className="mt-5 space-y-2.5">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isPopular ? "bg-amber-400 text-slate-900" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className="font-medium">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  onClick={() => startCheckout(tier)}
                  disabled={isBusy || isCurrent || polling}
                  className={`mt-6 w-full h-12 rounded-2xl font-black tracking-wide transition-all hover:scale-[1.01] active:scale-[0.99] ${
                    isPopular
                      ? "text-slate-900 shadow-lg shadow-amber-400/40 hover:shadow-xl"
                      : tier.id === "recruit"
                        ? "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                  style={
                    isPopular
                      ? { background: "linear-gradient(135deg,#FBBF24,#F59E0B)", fontFamily: "Outfit, sans-serif" }
                      : { fontFamily: "Outfit, sans-serif" }
                  }
                  data-testid={`upgrade-button-${tier.id}`}
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isCurrent ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="w-4 h-4" /> Your current plan
                    </span>
                  ) : tier.id === "recruit" ? (
                    "Free forever"
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Zap className="w-4 h-4" strokeWidth={3} /> Upgrade Now
                    </span>
                  )}
                </Button>

                {tier.id === "legend" && (
                  <p className="mt-3 text-[11px] text-center text-slate-500 inline-flex items-center justify-center w-full gap-1">
                    <Users className="w-3 h-3" /> Up to 4 family members included
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400 leading-relaxed">
          Payments secured by Stripe · Test mode active · Cancel anytime from your dashboard.
        </p>
      </div>

      {/* Cancel confirmation */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="rounded-3xl border-2 border-slate-100" data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle
              className="text-2xl font-black text-slate-900"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Cancel your Hero plan?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
              You'll keep {currentTier.toUpperCase()} powers until{" "}
              <span className="font-bold text-slate-900">{formatPeriodEnd(currentPeriodEnd)}</span>.
              After that you'll return to the free Recruit tier (2 defeats max, no bank auto-sync).
              You can reactivate anytime before the period ends.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 flex-col sm:flex-row">
            <AlertDialogCancel
              className="rounded-xl h-11 font-bold"
              disabled={cancelBusy}
              data-testid="cancel-dialog-keep"
            >
              Keep my plan
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doCancel}
              disabled={cancelBusy}
              className="rounded-xl h-11 font-bold bg-red-500 hover:bg-red-600 text-white"
              data-testid="cancel-dialog-confirm"
            >
              {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Yes, cancel plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
